import { useState } from 'react';
import { Select, Button, Tooltip } from '@bancobarigui/barihaus';
import { HolderOutlined, EditOutlined } from '@ant-design/icons';
import { Question, QuestionType } from '../types';
import { EditableTitle } from '../shared/components';

const questionTypeLabels: Record<QuestionType, string> = {
  [QuestionType.SHORT_TEXT]: 'Resposta curta',
  [QuestionType.LONG_TEXT]: 'Resposta longa',
  [QuestionType.RADIO]: 'Opção única',
  [QuestionType.CHECKBOX]: 'Múltipla seleção',
  [QuestionType.YES_NO]: 'Sim/Não',
  [QuestionType.DATE]: 'Data',
  [QuestionType.INTEGER]: 'Numérico inteiro',
  [QuestionType.DECIMAL]: 'Numérico decimal',
  [QuestionType.CURRENCY]: 'Moeda',
  [QuestionType.TABLE]: 'Tabela',
  [QuestionType.PHOTO]: 'Foto',
  [QuestionType.FILE]: 'Arquivo',
};

interface QuestionHeaderProps {
  question: Question;
  onEdit: (questionId: string, updates: Partial<Question>) => void;
  dragHandleProps: any;
}

export function QuestionHeader({ question, onEdit, dragHandleProps }: QuestionHeaderProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(question.title);

  const handleStartEdit = () => {
    setEditValue(question.title);
    setIsEditing(true);
  };

  const handleSave = () => {
    if (editValue.trim()) {
      onEdit(question.id, { title: editValue.trim() });
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(question.title);
    setIsEditing(false);
  };

  return (
    <div className="flex items-center justify-between gap-3 mb-4">
      <div className="flex items-center gap-2 flex-1">
        <div {...dragHandleProps} className="cursor-move">
          <HolderOutlined className="text-gray-400 text-lg" />
        </div>

        {isEditing ? (
          <EditableTitle
            value={editValue}
            onChange={setEditValue}
            onSave={handleSave}
            onCancel={handleCancel}
            size="large"
            placeholder="Nome da pergunta"
          />
        ) : (
          <div className="flex items-center gap-2 flex-1">
            <h5 className="text-lg font-semibold text-gray-700 m-0">
              {question.title}
            </h5>
            <Tooltip title="Editar nome da pergunta">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={handleStartEdit}
                children={undefined}
              />
            </Tooltip>
          </div>
        )}
      </div>

      <Select
        value={question.answerType}
        onChange={(answerType) => {
          const updates: Partial<Question> = { answerType: answerType as QuestionType };

          // Inicializar opções com valores descritivos quando mudar para tipos baseados em opções
          if ((answerType === QuestionType.RADIO || answerType === QuestionType.CHECKBOX) &&
              (!question.options || question.options.length === 0)) {
            updates.options = ['Opção 1', 'Opção 2'];
          }

          onEdit(question.id, updates);
        }}
        size="middle"
        className="w-56"
        options={Object.entries(questionTypeLabels).map(([value, label]) => ({
          value,
          label,
          disabled: false,
        }))}
      />
    </div>
  );
}
